import {
  Box,
  BoxProps,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
} from '@chakra-ui/react'
import { FilterCondition } from '@helo/cms'
import { format, isValid } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLilius } from 'use-lilius'

import { FilterCriteria } from '../../services/queryService'
import { formatUTCDate, parseUTCDateString } from '../../utils/dates'
import { ChevronIcon } from '../Icons/ChevronIcon'
import { Icon } from '../Icons/Icon'
import { PopoverCard } from '../PopoverCard/PopoverCard'
import { YearMonthSelector } from '../YearMonthSelector/YearMonthSelector'
import { dateToIso } from '../inputs/DateInput'
import { Pill, PillSelect, PillSelectOption } from '../inputs/PillSelect'
import { SingleDateInput } from '../inputs/SingleDateInput/SingleDateInput'
import { Divider } from './Calendar-parts'
import { defaultFilterCriteria } from './DateFilterEditor'
import { SingleSelectCalendar } from './SingleSelectCalendar'

const pillOptions = [
  { label: 'Before', value: FilterCondition.lessThan },
  { label: 'After', value: FilterCondition.greaterThan },
  { label: 'Excludes', value: FilterCondition.notEqual },
] satisfies PillSelectOption[]

export type SingleDateCalendarProps = BoxProps & {
  filterCriteria?: FilterCriteria
  columnId?: string
  onUpdate?: (criteria: FilterCriteria) => void
  /** When true (default), show Before/After/Excludes pills in input and popover; when false, condition is chosen in parent. */
  showConditionPills?: boolean
  /** Click-outside boundary ID so selecting a date doesn't close a parent popover (e.g. filter editor). */
  clickBoundaryId?: string
  /** When set, calendar renders into this container (e.g. chip popover) so clicks stay "inside". */
  containerRef?: React.RefObject<HTMLElement | null>
}

export const SingleDateCalendar = ({
  onUpdate = () => {},
  filterCriteria = defaultFilterCriteria,
  columnId,
  showConditionPills = true,
  clickBoundaryId,
  containerRef,
}: SingleDateCalendarProps) => {
  const lilus = useLilius()
  const {
    selected,
    setViewing,
    viewing,
    viewNextMonth,
    viewPreviousMonth,
    setSelected,
  } = lilus

  const [isCalOpen, setIsCalOpen] = useState<boolean>(false)
  const [isEnabledUseOutsideClick, setIsEnabledUseOutsideClick] =
    useState<boolean>(true)
  const singleDateInputRef = useRef<HTMLInputElement>(null)

  const inputContainerRef = useRef<HTMLInputElement>(null)
  const calContainerRef = useRef<HTMLInputElement>(null)
  const calPopoverCardRef = useRef<HTMLDivElement | null>(null)

  const [applyIsDisabled, setApplyIsDisabled] = useState<boolean>(true)

  const [pillSelected, setPillSelected] = useState<FilterCondition>(
    FilterCondition.equals,
  )
  const [errorMsg, setErrorMsg] = useState<string>('')

  const filterValue = filterCriteria?.filterValue as string | undefined
  useEffect(() => {
    if (filterValue) {
      const filterValueDateFormatted = parseUTCDateString(filterValue)
      if (filterValueDateFormatted) {
        setSelected([filterValueDateFormatted])
        if (singleDateInputRef.current) {
          singleDateInputRef.current.value = formatUTCDate(
            filterValueDateFormatted,
            'MM-dd-yyyy',
          )
        }
      }
    } else {
      setSelected([])
      if (singleDateInputRef.current) {
        singleDateInputRef.current.value = ''
      }
    }
  }, [filterValue, setSelected])

  const effectiveCondition = showConditionPills
    ? (pillSelected as FilterCondition)
    : (filterCriteria.condition as FilterCondition)

  const syncValueAndClose = () => {
    if (selected[0] && isCalOpen) {
      if (singleDateInputRef.current) {
        singleDateInputRef.current.value = format(selected[0], 'MM-dd-yyyy')
      }
      setApplyIsDisabled(false)
      onUpdate({
        ...filterCriteria,
        columnId,
        filterValue: dateToIso(selected[0]),
        condition: effectiveCondition,
      })
    }
    setIsCalOpen(false)
  }

  const isClickInsideCalendar = useCallback((target: Node | null) => {
    if (!target || !(target instanceof Element)) return false
    // Only treat clicks inside THIS calendar's own popover card as "inside".
    // Matching any [data-popover-card] ancestor would wrongly include the parent
    // filter card, so clicking sibling controls (e.g. the condition dropdown)
    // would fail to close the calendar.
    const insidePopover = calPopoverCardRef.current?.contains(target) ?? false
    const insideCal = calContainerRef.current?.contains(target) ?? false
    const insideInput = inputContainerRef.current?.contains(target) ?? false
    return insidePopover || insideCal || insideInput
  }, [])

  useEffect(() => {
    if (!isCalOpen || !isEnabledUseOutsideClick) return
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (isClickInsideCalendar(target)) return
      syncValueAndClose()
    }
    document.addEventListener('mousedown', onDocMouseDown, true)
    return () => document.removeEventListener('mousedown', onDocMouseDown, true)
  }, [isCalOpen, isEnabledUseOutsideClick, selected, isClickInsideCalendar])

  return (
    <Box ref={inputContainerRef} alignSelf="stretch" minWidth={0}>
      <Box
        alignSelf="stretch"
        onClick={() => {
          setIsCalOpen(true)
          setErrorMsg('')
        }}
      >
        <SingleDateInput
          color={errorMsg !== '' ? 'presets.pink.70' : 'none'}
          ref={singleDateInputRef}
          onBlur={(e: React.FocusEvent) => {
            const relatedTarget = e.relatedTarget as HTMLElement | null
            if (
              relatedTarget &&
              calContainerRef.current?.contains(relatedTarget)
            ) {
              return
            }
            // Clicking a date cell often moves focus to body (cell not focusable); don't close
            if (
              relatedTarget &&
              calPopoverCardRef.current?.contains(relatedTarget)
            ) {
              return
            }
            if (relatedTarget === null) return
            const inputValue = singleDateInputRef.current?.value
            if (inputValue && inputValue.length === 10) {
              const [m, d, y] = inputValue.split('-')
              const convertedDate = new Date(`${m} ${d}, ${y} 00:00:00`)
              if (isValid(convertedDate)) {
                onUpdate({
                  ...filterCriteria,
                  columnId,
                  filterValue: dateToIso(convertedDate),
                  condition: effectiveCondition,
                })
              }
            }
            setIsCalOpen(false)
          }}
          rightInputElement={
            <Icon
              data-testid="single-date-calendar-toggle"
              cursor="pointer"
              onMouseEnter={() => setIsEnabledUseOutsideClick(false)}
              onMouseLeave={() => setIsEnabledUseOutsideClick(true)}
              onClick={() => {
                setTimeout(() => setIsCalOpen(!isCalOpen), 0)
              }}
              icon="CalendarIcon"
              boxSize="14px"
              color={isCalOpen ? 'presets.turquoise.50' : 'presets.neutral.60'}
            />
          }
          onChange={value => {
            if (typeof value === 'string' && value.length === 10) {
              const [m, d, y] = value.split('-')
              const convertedDate = new Date(`${m} ${d}, ${y} 00:00:00`)
              if (isValid(convertedDate)) {
                setViewing(convertedDate)
                setSelected([convertedDate])
                setApplyIsDisabled(false)
              }
            } else {
              setSelected([])
              setApplyIsDisabled(true)
            }
          }}
          leftInputElement={
            showConditionPills &&
            pillOptions.filter(pill => pill.value === filterCriteria.condition)
              .length ? (
              <Pill isSelected={true} size="sm" marginY="-5px">
                {
                  pillOptions.filter(
                    pill => pill.value === filterCriteria.condition,
                  )[0].label
                }
              </Pill>
            ) : undefined
          }
        />
      </Box>
      {errorMsg !== '' && (
        <Text fontSize="10px" fontWeight="500" color="presets.pink.70">
          {errorMsg}
        </Text>
      )}
      <PopoverCard
        padding="0px"
        referenceElement={inputContainerRef.current}
        isOpen={isCalOpen}
        onClose={() => {}}
        clickBoundaryId={clickBoundaryId}
        containerRef={containerRef}
        contentRef={calPopoverCardRef}
        data-testid="single-date-calendar-popover"
      >
        <Flex ref={calContainerRef} flexDirection="column">
          <Box padding="8px 16px 0px 16px">
            <Flex
              alignItems="center"
              justifyContent="space-between"
              paddingBottom="8px"
            >
              <IconButton
                aria-label="previous month"
                fontSize="16px"
                color="presets.neutral.60"
                padding="4px"
                ml="-12px"
                onClick={viewPreviousMonth}
                icon={<ChevronIcon transform="rotate(90deg)" />}
              />
              <YearMonthSelector
                currentMonth={viewing.getMonth()}
                currentYear={viewing.getFullYear()}
                setCalendar={setViewing}
              />
              <IconButton
                aria-label="next month"
                fontSize="16px"
                color="presets.neutral.60"
                padding="4px"
                mr="-12px"
                onClick={viewNextMonth}
                icon={<ChevronIcon transform="rotate(270deg)" />}
              />
            </Flex>
            <Divider />
            <SingleSelectCalendar
              pillSelected={
                showConditionPills ? pillSelected : effectiveCondition
              }
              lilus={lilus}
              date={selected[0] || new Date()}
              onUpdate={day => {
                if (singleDateInputRef.current && day) {
                  singleDateInputRef.current.value = format(day, 'MM-dd-yyyy')
                  setApplyIsDisabled(
                    singleDateInputRef.current.value.length !== 10,
                  )
                }
              }}
            />
            {showConditionPills ? (
              <Box marginBottom="8px">
                <PillSelect
                  options={pillOptions}
                  selected={pillSelected}
                  onChange={value => {
                    if (pillSelected === value) {
                      setPillSelected(FilterCondition.equals)
                    } else {
                      setPillSelected(value)
                    }
                  }}
                />
              </Box>
            ) : null}
          </Box>
          <Divider />
          <Flex padding="8px 16px">
            <HStack gap="8px" justifyContent="space-between" width="100%">
              <Button
                size="sm"
                variant="tertiary"
                onClick={() => {
                  setSelected([])
                  if (singleDateInputRef.current) {
                    singleDateInputRef.current.value = ''
                  }
                  setApplyIsDisabled(true)
                  onUpdate({
                    ...filterCriteria,
                    columnId,
                    filterValue: '',
                    condition: effectiveCondition,
                  })
                  setTimeout(() => setIsCalOpen(false), 0)
                }}
              >
                Clear
              </Button>
              <HStack gap="8px">
                <Button
                  size="sm"
                  variant="tertiary"
                  onClick={() => {
                    if (filterValue) {
                      const parsed = parseUTCDateString(filterValue)
                      if (parsed && singleDateInputRef.current) {
                        singleDateInputRef.current.value = formatUTCDate(
                          parsed,
                          'MM-dd-yyyy',
                        )
                        setSelected([parsed])
                      }
                    } else {
                      if (singleDateInputRef.current) {
                        singleDateInputRef.current.value = ''
                      }
                      setSelected([])
                    }
                    setApplyIsDisabled(!filterValue)
                    onUpdate({ ...filterCriteria, columnId })
                    setTimeout(() => setIsCalOpen(false), 0)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  isDisabled={applyIsDisabled}
                  onClick={() => {
                    if (selected[0]) {
                      onUpdate({
                        ...filterCriteria,
                        columnId,
                        filterValue: dateToIso(selected[0]),
                        condition: effectiveCondition,
                      })
                    } else {
                      setErrorMsg('Date is invalid format (mm-dd-yyyy)')
                    }
                    setTimeout(() => setIsCalOpen(false), 0)
                  }}
                >
                  Done
                </Button>
              </HStack>
            </HStack>
          </Flex>
        </Flex>
      </PopoverCard>
    </Box>
  )
}
